import React from 'react';
import { Select } from 'antd';
import { useLazyQuery, useQuery } from '@apollo/client';
import {
  GET_ALL_CHANNEL,
  SEARCH_CHANNELS_BY_NAME,
  allChannelResponse,
  SearchChannelsByNameResponse
} from '../../graphql/queries/getChannelAll';
import { useFilterLabelClassName, useFilterContainerClassName, useFilterInputContainerClassName } from '../../utils/languageUtils';

interface MultiClientSelectFilterProps {
  label: string;
  value?: string[];
  onChange: (value: string[] | undefined) => void;
  spanClassName?: string;
  placeholder?: string;
  className?: string;
}

export const MultiClientSelectFilter: React.FC<MultiClientSelectFilterProps> = ({
  label,
  value,
  onChange,
  spanClassName,
  placeholder = 'All',
  className
}) => {
  const { data: allData, loading: allLoading } = useQuery<allChannelResponse>(GET_ALL_CHANNEL);
  const [search, { data: searchData, loading: searchLoading }] = useLazyQuery<SearchChannelsByNameResponse>(SEARCH_CHANNELS_BY_NAME);

  const [searchText, setSearchText] = React.useState('');
  const debouncedKeyword = useDebounce(searchText, 300);

  React.useEffect(() => {
    if (debouncedKeyword && debouncedKeyword.trim().length > 0) {
      search({ variables: { keyword: debouncedKeyword.trim() } });
    }
  }, [debouncedKeyword, search]);

  const options = React.useMemo(() => {
    const list = (debouncedKeyword && searchData?.searchChannelsByName?.length)
      ? searchData.searchChannelsByName
      : allData?.getAllChannels || [];

    return list.map(ch => ({
      label: ch.channelName,
      value: ch.id,
      searchText: ch.channelName
    }));
  }, [allData, searchData, debouncedKeyword]);

  // 使用统一的样式 Hook，根据语言自动返回合适的样式类名
  const containerClassName = useFilterContainerClassName(className);
  const labelClassName = useFilterLabelClassName(spanClassName);
  const inputContainerClassName = useFilterInputContainerClassName();

  return (
    <div className={containerClassName}>
      <span className={labelClassName}>{label}</span>
      <div className={inputContainerClassName}>
        <Select
          mode="multiple"
          showSearch
          allowClear
          placeholder={placeholder}
          value={value}
          onChange={(val) => onChange(val && val.length > 0 ? val : undefined)}
          loading={allLoading || searchLoading}
          options={options}
          filterOption={false}
          onSearch={(val) => setSearchText(val)}
          className="w-full"
          size="middle"
          maxTagCount="responsive"
          maxTagTextLength={10}
          tagRender={(props) => {
            const { label, closable, onClose } = props;
            return (
              <span className="ant-select-selection-item inline-flex items-center bg-gray-100 text-gray-800 rounded px-2 py-1 text-sm mr-1 mb-1">
                <span className="ant-select-selection-item-content truncate max-w-[120px]">{label}</span>
                {closable && (
                  <span 
                    className="ant-select-selection-item-remove ml-1 cursor-pointer hover:text-red-500" 
                    onClick={onClose}
                  >
                    ×
                  </span>
                )}
              </span>
            );
          }}
        />
      </div>
    </div>
  );
};

function useDebounce<T>(value: T, delay = 300): T {
  const [debounced, setDebounced] = React.useState(value);
  React.useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

export default MultiClientSelectFilter;
